/**
 * Super admin overview numbers and plain-language activity.
 * Revenue uses active paid subscriptions only. Trials are not revenue.
 */

const SOLO_MONTHLY = 49;

const TEAM_MONTHLY = {
  2: 98,
  3: 133,
  4: 168,
  5: 203,
  6: 238,
  7: 273,
  8: 308,
  9: 343,
  10: 378,
};

const ACTION_PHRASES = {
  update_subscription: 'changed a subscription',
  delete_agent: 'removed an agent',
  update_agent: 'updated an agent profile',
  update_master_template: 'updated a template',
  create_master_template: 'added a template',
  delete_master_template: 'removed a template',
  update_vendor: 'updated a vendor',
  delete_vendor: 'removed a vendor',
  create_global_task: 'added a global task',
  update_global_task: 'updated a global task',
  deactivate_global_task: 'turned a global task off',
  activate_global_task: 'turned a global task on',
  delete_global_task: 'removed a global task',
  create_discount_code: 'issued a discount code',
  deactivate_discount_code: 'turned a discount code off',
  activate_discount_code: 'turned a discount code on',
  update_landing_page_photo: 'changed a landing page photo',
  update_landing_page_text: 'changed landing page text',
};

const APPLICATION_STATUS = {
  invited: 'Invite sent',
  submitted: 'Waiting for review',
  approved: 'Accepted',
  declined: 'Declined',
};

const INQUIRY_STATUS = {
  pending: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Became a customer',
  declined: 'Declined',
};

function teamMonthly(seats) {
  const count = Number(seats);
  if (!Number.isInteger(count) || count < 2 || count > 10) return null;
  return TEAM_MONTHLY[count];
}

function isTeamOwner(profile) {
  return Boolean(profile) && (profile.is_team_owner === true || profile.role === 'owner');
}

function paidSubscriptions(profiles, teams) {
  const teamById = new Map((teams || []).map((team) => [team.id, team]));
  const seenTeams = new Set();
  const rows = [];

  (profiles || []).forEach((profile) => {
    if (!profile || profile.subscription_status !== 'active') return;
    const plan = profile.subscription_plan;

    if (profile.team_id && (plan === 'team' || plan === 'solo')) {
      if (!isTeamOwner(profile) || seenTeams.has(profile.team_id)) return;
      seenTeams.add(profile.team_id);
      if (plan === 'solo') {
        rows.push({ plan: 'solo', amount: SOLO_MONTHLY });
        return;
      }
      const amount = teamMonthly(teamById.get(profile.team_id) && teamById.get(profile.team_id).max_seats);
      if (amount == null) return;
      rows.push({ plan: 'team', amount });
      return;
    }

    if (plan === 'solo') {
      rows.push({ plan: 'solo', amount: SOLO_MONTHLY });
      return;
    }

    if (plan === 'team') {
      const amount = teamMonthly(profile.max_seats);
      if (amount == null) return;
      rows.push({ plan: 'team', amount });
    }
  });

  return rows;
}

function monthlyRevenue(profiles, teams) {
  const rows = paidSubscriptions(profiles, teams);
  const amount = rows.reduce((sum, row) => sum + row.amount, 0);
  const paidCount = rows.length;
  const noun = paidCount === 1 ? 'subscription' : 'subscriptions';
  return {
    amount,
    paidCount,
    summary: `from ${paidCount} active paid ${noun}`,
  };
}

function humanize(value) {
  return String(value || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
}

function detailPhrase(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return '';
  const parts = [];
  if (details.name) parts.push(String(details.name));
  if (details.title) parts.push(String(details.title));
  if (details.first_name || details.last_name) {
    parts.push([details.first_name, details.last_name].filter(Boolean).join(' '));
  }
  if (details.photoType) parts.push(`${humanize(details.photoType)} photo`);
  if (details.field) parts.push(humanize(details.field));
  if (details.code) parts.push(String(details.code));
  if (details.subscription_plan) parts.push(`${details.subscription_plan} plan`);
  if (details.subscription_status) parts.push(String(details.subscription_status));
  if (!parts.length) return '';
  return `: ${parts.join(', ')}`;
}

function activitySentence(log) {
  const entry = log || {};
  const admin = entry.admin || {};
  const name = [admin.first_name, admin.last_name].filter(Boolean).join(' ').trim() || 'An admin';
  const known = ACTION_PHRASES[entry.action_type];
  const phrase = known || (humanize(entry.action_type) ? `recorded ${humanize(entry.action_type)}` : 'recorded an action');
  return `${name} ${phrase}${detailPhrase(entry.details)}.`;
}

function applicationStatusLabel(status) {
  return APPLICATION_STATUS[status] || 'Unknown';
}

const APPLICATION_FILTERS = [
  { id: 'submitted', label: 'Waiting' },
  { id: 'invited', label: 'Invite sent' },
  { id: 'approved', label: 'Accepted' },
  { id: 'declined', label: 'Declined' },
  { id: 'all', label: 'All' },
];

function teamCount(teams) {
  return Array.isArray(teams) ? teams.length : 0;
}

function applicationsForFilter(applications, filter) {
  const rows = Array.isArray(applications) ? applications : [];
  if (filter === 'all') return rows;
  return rows.filter((row) => row && row.status === filter);
}

function applicationFilterSubtitle(filter) {
  if (filter === 'submitted') return 'Vendor applications waiting on a decision.';
  if (filter === 'invited') return 'Invites that have not been submitted yet.';
  if (filter === 'approved') return 'Accepted vendor applications.';
  if (filter === 'declined') return 'Declined vendor applications.';
  return 'Every vendor application.';
}

function inquiryStatusLabel(status) {
  return INQUIRY_STATUS[status] || 'Unknown';
}

function applicationCard(application) {
  const row = application || {};
  const name = String(row.business_name || '').trim();
  const email = String(row.email || '').trim();
  const trades = Array.isArray(row.trades)
    ? row.trades.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  return {
    title: name || email || 'Invite pending',
    email: name && email ? email : '',
    pending: !name && !email,
    trades: trades.join(', '),
  };
}

export {
  SOLO_MONTHLY,
  TEAM_MONTHLY,
  teamMonthly,
  paidSubscriptions,
  monthlyRevenue,
  activitySentence,
  applicationStatusLabel,
  inquiryStatusLabel,
  APPLICATION_FILTERS,
  teamCount,
  applicationsForFilter,
  applicationFilterSubtitle,
  applicationCard,
};
